import { createSelector } from '@reduxjs/toolkit';
import { PROTOCOL_PENDING_STATUS } from '../../constants';
import { graphToDocument } from '@miroculus/nucleo';
import { uniqBy } from 'ramda';
/*
  The keys of this object match with the
  stored names in the API DB
*/
const CARTRIDGE_NAME_MAP = {
    Miro: 'revA',
    Canvas: 'revB',
};
export const currentProtocolSelector = ({ protocols }) => protocols.currentProtocol;
const selectCurrentProtocolGraphActions = ({ protocols }) => protocols.currentProtocol.graph?.actions;
/**
 * Gets the reagents that this protocol uses, with aggregated volume
 * @param state Redux store state
 */
export const getProtocolReagents = createSelector([selectCurrentProtocolGraphActions], (actions) => Object.values((actions || [])
    .filter((a) => a.type === 'element')
    .reduce((reagentMap, { description, volume = 0, jacketVol = 0 }) => ({
    ...reagentMap,
    [description || '']: {
        description: description || '',
        jacketVol: jacketVol + (reagentMap[description || '']?.jacketVol ?? 0),
        volume: volume + (reagentMap[description || '']?.volume ?? 0),
    },
}), {})));
// Helper selectors for memoization
const selectGraphActions = ({ protocols }) => protocols.currentProtocol.graph?.actions;
const selectLibraryReagents = ({ protocols }) => protocols.currentProtocol.library?.reagents;
/**
 * Gets the reagents that this protocol uses + reagents added by the user
 * in this session
 * @param state Redux store state
 */
export const getReagents = createSelector([selectGraphActions, selectLibraryReagents], (actions, libraryReagents) => uniqBy(({ name }) => name, [
    ...(actions || [])
        .filter((a) => a.type === 'element')
        .map(({ description }) => ({ name: description || '' })),
    ...(libraryReagents || []),
]));
/**
 * Gets the action modules stored in the state (currently loaded per-workspace)
 * @param state Redux store state
 */
export const getActionModules = ({ protocols }) => protocols.currentProtocol.library.actionModules;
export const protocolTimeSelector = ({ protocols }) => {
    if (protocols.currentProtocol.time)
        return protocols.currentProtocol.time;
    if (!protocols.currentProtocol.graph)
        return 0;
    let total = 0;
    protocols.currentProtocol.graph.actions.forEach((action) => {
        const cyclesFactor = action.cycles || 1;
        total =
            total +
                cyclesFactor * (action.time || 0) +
                cyclesFactor * (action.washTime || 0) +
                cyclesFactor * (action.incuTime || 0) +
                cyclesFactor * (action.dryTime || 0);
        // Thermocycle
        if (action.steps) {
            total =
                total +
                    action.steps.map((step) => step.time).reduce((a, b) => a + b, 0) *
                        cyclesFactor;
        }
    });
    return total;
};
const selectCurrentGraph = ({ protocols }) => protocols.currentProtocol.graph;
export const paletteDocSelector = createSelector([selectCurrentGraph], (graph) => graphToDocument(graph));
const selectValidationErrors = ({ protocols }) => protocols.currentProtocol.validationErrors;
export const getProtocolValidationErrors = createSelector([selectValidationErrors], (validationErrors) => validationErrors.filter((e) => e.level === 'error'));
export const getProtocolValidationWarnings = createSelector([selectValidationErrors], (validationErrors) => validationErrors.filter((e) => e.level === 'warning'));
export const getProtocolErrorCycleIndex = ({ protocols }) => protocols.currentProtocol.errorCycleIndex;
export const getProtocolWarningCycleIndex = ({ protocols }) => protocols.currentProtocol.warningCycleIndex;
// a protocol is considered "original" if it was generated from a template
// (aka "dynamic labscript") and hasn't been modified since then
// NOTE: we assume that if the updatedAt and createdAt are close enough in time
// then it was never updated
const isOriginal = (protocol) => !!protocol.template && Math.abs(new Date(protocol.updatedAt || 0).getTime() - new Date(protocol.createdAt || 0).getTime()) < 1000;
const selectFilteredProtocols = ({ protocols }) => protocols.filteredProtocols;
export const getProtocolsToDisplay = createSelector([selectFilteredProtocols], (filteredProtocols) => filteredProtocols.map((protocol) => ({
    ...protocol.$permissions,
    type: protocol.type,
    id: protocol.id,
    name: protocol.name,
    time: protocol.time,
    createdAt: protocol.createdAt,
    cartridge: CARTRIDGE_NAME_MAP[protocol.cartridge?.name || ''] || protocol.cartridge?.name || '',
    author: protocol.author,
    solutionStatus: protocol.solutionStatus || PROTOCOL_PENDING_STATUS,
    version: protocol.version,
    team: protocol.team,
    visibility: protocol.visibility,
    original: isOriginal(protocol),
})));
const selectProtocols = ({ protocols }) => protocols.protocols;
const selectProtocolTypes = ({ protocols }) => protocols.protocolTypes;
export const getUsedProtocolTypes = createSelector([selectProtocols, selectProtocolTypes], (protocols, protocolTypes) => {
    const usedTypes = protocols.reduce((set, p) => set.add(p.type?.id), new Set());
    return protocolTypes.filter((t) => usedTypes.has(t.value));
});
const selectCurrentSolution = ({ protocols }) => protocols.currentProtocol.solution;
export const pathsSelector = createSelector([selectCurrentSolution], (solution) => {
    if (!solution || !solution.paths) {
        return [];
    }
    return solution.paths
        .map((path, index) => {
        return path.map((electrode) => [electrode, index]);
    })
        .reduce((a, b) => a.concat(b), []);
});
/**
 * returns true if the current protocol has solution or the solution is loading
 */
export const loadingOrSolutionObtained = ({ protocols }) => {
    const { currentProtocol, loadingSolution } = protocols;
    if (!currentProtocol)
        return false;
    else
        return !!(currentProtocol.solution || loadingSolution);
};
export const protocolHasTemplate = ({ protocols }) => !!protocols.currentProtocol.template;
